import type { ScheduleState } from "@/pages/human-resources/time-management/business/schedule/ScheduleState.ts";
import type { ScheduleRange } from "@/pages/human-resources/time-management/model/ScheduleRange.ts";
import { legacyFormatFrenchDate } from "@/shared/utils/LegacyDates.ts";
import { DateTime, type DateTimeUnit } from "luxon";
import type { UserPermissions } from "@/shared/model/user/UserPermissions.ts";
import type { Site } from "@/shared/model/user/UserDomains.ts";
import {
  RH_PLANNING_FICHE_HORAIRE_HEBDO_VISU,
  RH_PLANNING_FICHE_HORAIRE_MENSUELLE_VISU,
  RH_PLANNING_FICHE_HORAIRE_VISU,
  RH_PLANNING_VISU,
} from "@/shared/model/user/permissions/Permissions.ts";
import useUserPermissions from "@/shared/model/user/hooks/useUserPermissions.ts";

type DojoScheduleUrlBuilder = (
  site: Site | undefined,
  type: ScheduleState,
  range: ScheduleRange,
  date: Date | undefined,
) => string | undefined;

export default function useDojoScheduleUrlBuilder(): DojoScheduleUrlBuilder {
  const userPermissions = useUserPermissions();
  return (
    site: Site | undefined,
    type: ScheduleState,
    range: ScheduleRange,
    date: Date | undefined,
  ) => {
    if (
      site === undefined ||
      date === undefined ||
      !canAccessScheduleView(userPermissions, site, type, range)
    ) {
      return undefined;
    }
    const isAchieved = type === "ACHIEVED";
    const urlParams = new URLSearchParams({
      idSite: site.id,
      task: "view",
      etat: isAchieved ? "3" : "1",
      compar: isAchieved ? "1" : "0",
      vue: mapRangeToVue(isAchieved, range),
      date: legacyDateParam(date, range),
    });
    return `/content/planning?${urlParams.toString()}`;
  };
}

function mapRangeToVue(isAchieved: boolean, range: ScheduleRange): string {
  switch (range) {
    case "DAY":
      return isAchieved ? "jourcases" : "jour";
    case "WEEK":
      return "semaine";
    case "MONTH":
      return "mois";
  }
}

function legacyDateParam(date: Date, range: ScheduleRange): string {
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  return legacyFormatFrenchDate(
    DateTime.fromJSDate(date)
      .startOf(rangeToDateTimeUnit(range), { useLocaleWeeks: false })
      .toJSDate(),
  );
}

function canAccessScheduleView(
  permissions: UserPermissions,
  site: Site,
  type: ScheduleState,
  range: ScheduleRange,
): boolean {
  if (!permissions.canAccessSiteId(site.id)) return false;

  return (
    permissions
      .ofDomainId(site.domainId)
      ?.hasEveryPermissions(requiredFor(type, range)) ?? false
  );
}

function requiredFor(type: ScheduleState, range: ScheduleRange): string[] {
  const res = [RH_PLANNING_VISU];
  if (type === "ACHIEVED") {
    switch (range) {
      case "DAY": {
        res.push(RH_PLANNING_FICHE_HORAIRE_VISU);
        break;
      }
      case "WEEK": {
        res.push(RH_PLANNING_FICHE_HORAIRE_HEBDO_VISU);
        break;
      }
      case "MONTH": {
        res.push(RH_PLANNING_FICHE_HORAIRE_MENSUELLE_VISU);
        break;
      }
    }
  }
  return res;
}

function rangeToDateTimeUnit(range: ScheduleRange): DateTimeUnit {
  switch (range) {
    case "DAY":
      return "day";
    case "WEEK":
      return "week";
    case "MONTH":
      return "month";
  }
}
